import { useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Check, Edit, Trash2, Plus, Calendar, User, Tag, Sparkles, ArrowLeft, Loader, AlertCircle, ChevronDown } from 'lucide-react'
import { Dialog } from '@headlessui/react';
import { supabase } from '../lib/supabase';
import { trelloService, TrelloBoard, TrelloList } from '../lib/trello';
import TrelloConnect from './TrelloConnect';
import MemberSelector from './MemberSelector';
import BoardSelector from './BoardSelector';
import LabelSelector from './LabelSelector';
import { TrelloLabel, TaskWithLabels, LabelSuggestion } from '../types/labels';
import { AILabelAnalyzer } from '../lib/aiLabelAnalysis';

interface OriginalTask {
  id?: string;
  title: string;
  description: string;
  assignee?: string;
  due_date?: string;
  priority: 'low' | 'medium' | 'high';
  labels: string[];
  status: 'pending' | 'approved' | 'rejected';
}

type WorkflowStep = 'select-board' | 'analyze-tasks' | 'review-tasks';

export default function TaskReviewEnhanced() {
  const location = useLocation()
  const navigate = useNavigate()
  
  // Workflow state
  const [currentStep, setCurrentStep] = useState<WorkflowStep>('select-board');
  const [originalTasks, setOriginalTasks] = useState<OriginalTask[]>([])
  const [enhancedTasks, setEnhancedTasks] = useState<TaskWithLabels[]>([])
  
  // Board and label state
  const [boards, setBoards] = useState<TrelloBoard[]>([]);
  const [lists, setLists] = useState<TrelloList[]>([]);
  const [selectedBoard, setSelectedBoard] = useState<string>('');
  const [availableLabels, setAvailableLabels] = useState<TrelloLabel[]>([]);
  
  // Loading and error states
  const [loading, setLoading] = useState(false)
  const [analyzingTasks, setAnalyzingTasks] = useState(false);
  const [creatingCards, setCreatingCards] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState('')
  
  // UI state
  const [showTrelloReconnect, setShowTrelloReconnect] = useState(false);
  const [editingTask, setEditingTask] = useState<string | null>(null);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null); // Track which dropdown is open

  useEffect(() => {
    // Get tasks from navigation state
    if (location.state?.tasks) {
      setOriginalTasks(location.state.tasks)
      setMessage(location.state.message || '')
      initializeTrelloConnection();
    } else {
      // If no tasks, redirect back to upload
      navigate('/dashboard')
    }
  }, [location.state, navigate])

  // Close any open dropdown when another one opens
  const handleDropdownToggle = (dropdownId: string) => {
    setOpenDropdown(openDropdown === dropdownId ? null : dropdownId);
  };

  useEffect(() => {
    // Close dropdowns when clicking outside
    const handleClickOutside = () => {
      setOpenDropdown(null);
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const initializeTrelloConnection = async () => {
    setLoading(true);
    try {
      // Check if user is connected to Trello
      const { data: user, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('Not authenticated');
      }

      // Initialize Trello service to load token from database
      await trelloService.init();

      // First check if Trello service is connected
      if (!trelloService.isConnected()) {
        setShowTrelloReconnect(true);
        return;
      }

      // Fetch user's boards
      const boardsData = await trelloService.getBoards();
      setBoards(boardsData || []);

      // Only show reconnect if we have no boards AND the connection failed
      if (!boardsData || boardsData.length === 0) {
        // Try to test the connection
        try {
          await trelloService.testConnection();
          // If test passes but no boards, that's okay - user might just have no boards
          console.log('✅ Trello connection working but no boards found');
        } catch (testError) {
          console.error('❌ Trello connection test failed:', testError);
          setShowTrelloReconnect(true);
        }
      }
    } catch (error) {
      console.error('Error initializing Trello connection:', error);
      setShowTrelloReconnect(true);
    } finally {
      setLoading(false);
    }
  };

  const handleBoardChange = async (boardId: string) => {
    setSelectedBoard(boardId);
    setLoading(true);

    try {
      // Fetch lists for the selected board
      const listsData = await trelloService.getBoardLists(boardId);
      setLists(listsData || []);

      // Fetch and ensure standard labels for the board
      const labelsData = await trelloService.ensureStandardLabels(boardId);
      const allLabels = [...labelsData.existing, ...labelsData.created];
      setAvailableLabels(allLabels);

    } catch (error) {
      console.error('Error fetching board data:', error);
      setError('Failed to load board information');
    } finally {
      setLoading(false);
    }
  };

  const handleListChange = (listId: string) => {
    // This function is no longer used for board selection, but kept for consistency
    // if (selectedBoard) {
    //   setSelectedList(listId);
    // }
  };

  const proceedToAnalysis = async () => {
    if (!selectedBoard) {
      setError('Please select a board');
      return;
    }

    setCurrentStep('analyze-tasks');
    setAnalyzingTasks(true);

    try {
      // Fetch lists for the selected board
      const listsData = await trelloService.getBoardLists(selectedBoard);
      setLists(listsData || []);

      // Fetch and ensure standard labels for the board
      const labelsData = await trelloService.ensureStandardLabels(selectedBoard);
      const allLabels = [...labelsData.existing, ...labelsData.created];
      setAvailableLabels(allLabels);

      // Fetch members just-in-time for this session
      console.log('🔄 Fetching members for task creation...');
      await trelloService.getMembers();
      console.log('✅ Members fetched and cached for this session');

      // Convert original tasks to enhanced format
      const tasksToAnalyze: TaskWithLabels[] = originalTasks.map((task, index) => ({
        id: task.id || `task-${index}`,
        title: task.title,
        description: task.description,
        priority: task.priority,
        labels: [], // Start with empty labels - user will select from available board labels
        assignee: task.assignee,
        due_date: task.due_date,
        selected: true,
        listId: listsData?.[0]?.id // Default to first list
      }));

      // Get AI suggestions for each task
      const aiSuggestions = await AILabelAnalyzer.analyzeBatchTasksForLabels(
        tasksToAnalyze,
        allLabels
      );

      // Combine tasks with AI suggestions
      const enhancedTasksList: TaskWithLabels[] = tasksToAnalyze.map((task, index) => ({
        ...task,
        aiSuggestions: aiSuggestions[index]
      }));

      setEnhancedTasks(enhancedTasksList);
      setCurrentStep('review-tasks');

    } catch (error) {
      console.error('Error analyzing tasks:', error);
      setError('Failed to analyze tasks with AI');
    } finally {
      setAnalyzingTasks(false);
    }
  };

  const handleTaskToggle = (taskId: string) => {
    setEnhancedTasks(tasks =>
      tasks.map(task =>
        task.id === taskId ? { ...task, selected: !task.selected } : task
      )
    );
  };

  const handleTaskUpdate = (taskId: string, updates: Partial<TaskWithLabels>) => {
    setEnhancedTasks(tasks =>
      tasks.map(task =>
        task.id === taskId ? { ...task, ...updates } : task
      )
    );
    // Don't close edit mode automatically - only close when user clicks Done/Cancel
  };

  const handleTaskEdit = (taskId: string, updates: Partial<TaskWithLabels>) => {
    handleTaskUpdate(taskId, updates);
    setEditingTask(null); // Close edit mode
  };

  const handleCreateLabel = async (name: string, color: string) => {
    if (!selectedBoard) return;

    try {
      const newLabel = await trelloService.createLabel(selectedBoard, name, color);
      setAvailableLabels(labels => [...labels, newLabel]);
    } catch (error) {
      console.error('Error creating label:', error);
      throw error;
    }
  };

  const handleCreateCards = async () => {
    const selectedTasks = enhancedTasks.filter(task => task.selected);
    
    if (selectedTasks.length === 0) {
      setError('Please select at least one task to create');
      return;
    }

    // Check if all selected tasks have a listId
    const tasksWithoutList = selectedTasks.filter(task => !task.listId);
    if (tasksWithoutList.length > 0) {
      setError('Please select a list for all tasks');
      return;
    }

    setCreatingCards(true);
    setError(null);

    try {
      const selectedBoardObj = boards.find(b => b.id === selectedBoard);

      if (!selectedBoardObj) {
        throw new Error('Invalid board selection');
      }

      const results = [];
      for (const task of selectedTasks) {
        try {
          const selectedListObj = lists.find(l => l.id === task.listId);
          if (!selectedListObj) {
            throw new Error(`Invalid list selection for task: ${task.title}`);
          }

          const cardData = {
            name: task.title,
            desc: task.description,
            idList: task.listId!,
            due: task.due_date, // Include due date
            idLabels: task.labels.map(labelName => {
              const label = availableLabels.find(l => l.name === labelName);
              return label?.id;
            }).filter(Boolean) as string[], // Filter out undefined values
            idMembers: task.assignee ? [task.assignee] : []
          };

          const result = await trelloService.createCard(cardData);
          
          // Save to card history with labels
          const { data: user } = await supabase.auth.getUser();
          if (user?.user) {
            // Get assignee name if member ID is provided
            let assigneeName = null;
            if (task.assignee) {
              // You can get member name from the MemberSelector's loaded members
              // For now, we'll store the ID and improve this later
              assigneeName = task.assignee; // Store ID for now, could be improved to store name
            }

            await supabase.from('card_history').insert({
              user_id: user.user.id,
              trello_card_id: result.id, // Use trello_card_id instead of card_id
              card_title: task.title, // Use card_title instead of card_name
              card_description: task.description,
              board_name: selectedBoardObj.name,
              list_name: selectedListObj.name,
              assignee_name: assigneeName, // Use assignee_name (it expects a name, not ID)
              due_date: task.due_date,
              labels: task.labels,
              ai_suggested_labels: task.aiSuggestions
            });

            // Update task record with Trello card ID to mark it as completed
            await supabase
              .from('tasks')
              .update({ 
                trello_card_id: result.id,
                trello_card_url: result.url,
                status: 'completed'
              })
              .eq('id', task.id);
          }

          results.push(result);
        } catch (taskError) {
          console.error(`Error creating card "${task.title}":`, taskError);
          results.push({ error: taskError instanceof Error ? taskError.message : 'Unknown error', task: task.title });
        }
      }

      // Navigate to success page with results
      navigate('/dashboard/success', {
        state: {
          results,
          board: selectedBoardObj.name,
          list: 'Multiple lists', // Since tasks can be in different lists
          totalTasks: selectedTasks.length
        }
      });

      // Clean up member cache after successful card creation
      console.log('🧹 Cleaning up member cache after card creation...');
      trelloService.clearMemberCache();

    } catch (error) {
      console.error('Error creating cards:', error);
      setError(`Failed to create cards: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setCreatingCards(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader className="h-8 w-8 animate-spin text-indigo-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading your Trello boards...</p>
        </div>
      </div>
    );
  }

  if (showTrelloReconnect) {
    return (
      <TrelloConnect 
        onConnect={(token) => {
          setShowTrelloReconnect(false);
          // Re-initialize the connection
          initializeTrelloConnection();
        }}
        onSkip={() => {
          setShowTrelloReconnect(false);
          // Navigate back to dashboard
          navigate('/dashboard');
        }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Dashboard
          </button>
          
          <h1 className="text-3xl font-bold text-gray-900">Review & Create Tasks</h1>
          <p className="mt-2 text-gray-600">
            {currentStep === 'select-board' && 'First, select where to create your tasks'}
            {currentStep === 'analyze-tasks' && 'Analyzing tasks with AI...'}
            {currentStep === 'review-tasks' && 'Review AI suggestions and create your tasks'}
          </p>
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center space-x-4">
            <div className={`flex items-center ${currentStep === 'select-board' ? 'text-indigo-600' : 'text-green-600'}`}>
              <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center ${
                currentStep === 'select-board' ? 'border-indigo-600 bg-indigo-50' : 'border-green-600 bg-green-50'
              }`}>
                {currentStep === 'select-board' ? '1' : <Check className="h-4 w-4" />}
              </div>
              <span className="ml-2 font-medium">Select Destination</span>
            </div>
            
            <div className={`h-px flex-1 ${currentStep !== 'select-board' ? 'bg-green-600' : 'bg-gray-300'}`}></div>
            
            <div className={`flex items-center ${
              currentStep === 'analyze-tasks' ? 'text-indigo-600' : 
              currentStep === 'review-tasks' ? 'text-green-600' : 'text-gray-400'
            }`}>
              <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center ${
                currentStep === 'analyze-tasks' ? 'border-indigo-600 bg-indigo-50' :
                currentStep === 'review-tasks' ? 'border-green-600 bg-green-50' : 'border-gray-300'
              }`}>
                {currentStep === 'review-tasks' ? <Check className="h-4 w-4" /> : 
                 currentStep === 'analyze-tasks' ? <Sparkles className="h-4 w-4" /> : '2'}
              </div>
              <span className="ml-2 font-medium">AI Analysis</span>
            </div>
            
            <div className={`h-px flex-1 ${currentStep === 'review-tasks' ? 'bg-green-600' : 'bg-gray-300'}`}></div>
            
            <div className={`flex items-center ${currentStep === 'review-tasks' ? 'text-indigo-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center ${
                currentStep === 'review-tasks' ? 'border-indigo-600 bg-indigo-50' : 'border-gray-300'
              }`}>
                3
              </div>
              <span className="ml-2 font-medium">Review & Create</span>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center">
            <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
            <span className="text-red-700">{error}</span>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-red-500 hover:text-red-700"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Step 1: Board Selection */}
        {currentStep === 'select-board' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Select Board
            </h2>
            
            <BoardSelector
              boards={boards.map(b => ({ id: b.id, name: b.name, url: b.url }))}
              selectedBoard={selectedBoard}
              onBoardChange={handleBoardChange}
              loading={loading}
            />

            <div className="mt-6 flex justify-between">
              <div className="text-sm text-gray-600">
                Found {originalTasks.length} tasks to process
              </div>
              <button
                onClick={proceedToAnalysis}
                disabled={!selectedBoard}
                className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                Continue to AI Analysis
                <Sparkles className="h-4 w-4 ml-2" />
              </button>
            </div>
          </div>
        )}

        {/* Step 2: AI Analysis */}
        {currentStep === 'analyze-tasks' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="text-center py-12">
              <Sparkles className="h-12 w-12 text-indigo-600 mx-auto mb-4 animate-pulse" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                AI is analyzing your tasks...
              </h2>
              <p className="text-gray-600 mb-4">
                Analyzing {originalTasks.length} tasks for priority and label suggestions
              </p>
              {analyzingTasks && (
                <div className="w-64 bg-gray-200 rounded-full h-2 mx-auto">
                  <div className="bg-indigo-600 h-2 rounded-full animate-pulse" style={{ width: '60%' }}></div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step 3: Task Review */}
        {currentStep === 'review-tasks' && (
          <div className="space-y-6">
            {enhancedTasks.map((task) => (
              <div
                key={task.id}
                className={`bg-white rounded-2xl border transition-all duration-200 hover:shadow-lg ${
                  task.selected ? 'border-indigo-300 shadow-md' : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="p-8">
                  {/* Task Header */}
                  <div className="flex items-start justify-between mb-6">
                    <div className="flex items-start space-x-4 flex-1">
                      <input
                        type="checkbox"
                        checked={task.selected}
                        onChange={() => handleTaskToggle(task.id)}
                        className="mt-1 h-5 w-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 transition-colors"
                      />
                      <div className="flex-1">
                        {editingTask === task.id ? (
                          <div className="space-y-4">
                            <input
                              type="text"
                              value={task.title}
                              onChange={(e) => handleTaskUpdate(task.id, { title: e.target.value })}
                              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-lg font-semibold"
                              placeholder="Task title"
                            />
                            <textarea
                              value={task.description}
                              onChange={(e) => handleTaskUpdate(task.id, { description: e.target.value })}
                              rows={3}
                              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                              placeholder="Task description"
                            />
                            <div className="flex items-center space-x-3">
                              <button
                                onClick={() => setEditingTask(null)}
                                className="px-4 py-2 bg-indigo-600 text-white rounded-xl text-sm font-medium hover:bg-indigo-700 transition-colors"
                              >
                                Save Changes
                              </button>
                              <button
                                onClick={() => setEditingTask(null)}
                                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-300 transition-colors"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-2">{task.title}</h3>
                            <p className="text-gray-600 leading-relaxed">{task.description}</p>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-3 ml-4">
                      {/* Priority Badge */}
                      {task.labels.some(label => ['High Priority', 'Medium Priority', 'Low Priority'].includes(label)) && (
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          task.labels.includes('High Priority') ? 'bg-red-100 text-red-800 border border-red-200' :
                          task.labels.includes('Medium Priority') ? 'bg-yellow-100 text-yellow-800 border border-yellow-200' :
                          'bg-green-100 text-green-800 border border-green-200'
                        }`}>
                          {task.labels.includes('High Priority') ? '🔴 High' :
                           task.labels.includes('Medium Priority') ? '🟡 Medium' : '🟢 Low'} Priority
                        </span>
                      )}
                      
                      {editingTask !== task.id && (
                        <button
                          onClick={() => setEditingTask(task.id)}
                          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-colors"
                          title="Edit task"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Task Configuration - Clean Column Layout */}
                  <div className="space-y-6">
                    {/* Due Date */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700 flex items-center">
                        <Calendar className="h-4 w-4 mr-2 text-gray-500" />
                        Due Date
                      </label>
                      <input
                        type="date"
                        value={task.due_date || ''}
                        onChange={(e) => handleTaskUpdate(task.id, { due_date: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>

                    {/* Labels */}
                    <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
                      <LabelSelector
                        availableLabels={availableLabels}
                        selectedLabels={task.labels}
                        onLabelsChange={(labels) => handleTaskUpdate(task.id, { labels })}
                        aiSuggestions={task.aiSuggestions}
                        onCreateLabel={handleCreateLabel}
                        isOpen={openDropdown === `labels-${task.id}`}
                        onToggle={() => handleDropdownToggle(`labels-${task.id}`)}
                      />
                    </div>

                    {/* Assignee */}
                    <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
                      <MemberSelector
                        value={task.assignee || ''}
                        onChange={(memberId) => handleTaskUpdate(task.id, { assignee: memberId })}
                        isOpen={openDropdown === `assignee-${task.id}`}
                        onToggle={() => handleDropdownToggle(`assignee-${task.id}`)}
                      />
                    </div>

                    {/* List Selection */}
                    <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
                      <label className="block text-sm font-medium text-gray-700 flex items-center">
                        <Tag className="h-4 w-4 mr-2 text-gray-500" />
                        Select List
                      </label>
                      <div className="relative">
                        <button
                          onClick={() => handleDropdownToggle(`list-${task.id}`)}
                          className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 flex items-center justify-between transition-all duration-200 ${
                            task.listId 
                              ? 'border-green-300 bg-green-50 text-green-800 shadow-sm' 
                              : 'border-gray-300 hover:border-gray-400 hover:shadow-sm'
                          }`}
                        >
                          <div className="flex items-center">
                            {task.listId ? (
                              <>
                                <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                                <span className="font-medium">{lists.find(l => l.id === task.listId)?.name}</span>
                              </>
                            ) : (
                              <>
                                <div className="w-3 h-3 bg-gray-300 rounded-full mr-3"></div>
                                <span className="text-gray-500">Choose a list...</span>
                              </>
                            )}
                          </div>
                          <ChevronDown className={`h-5 w-5 transition-transform duration-200 ${
                            openDropdown === `list-${task.id}` ? 'rotate-180' : ''
                          }`} />
                        </button>
                        
                        {openDropdown === `list-${task.id}` && (
                          <div className="absolute z-20 w-full mt-2 bg-white rounded-xl shadow-xl border border-gray-200 max-h-60 overflow-y-auto">
                            {lists.map(list => (
                              <button
                                key={list.id}
                                onClick={() => {
                                  handleTaskUpdate(task.id, { listId: list.id });
                                  handleDropdownToggle(`list-${task.id}`);
                                }}
                                className={`w-full px-4 py-3 text-left text-sm hover:bg-indigo-50 flex items-center justify-between transition-colors ${
                                  task.listId === list.id ? 'bg-indigo-50 text-indigo-700' : ''
                                }`}
                              >
                                <div className="flex items-center">
                                  <div className={`w-3 h-3 rounded-full mr-3 ${
                                    task.listId === list.id ? 'bg-indigo-500' : 'bg-gray-300'
                                  }`}></div>
                                  <span className="font-medium">{list.name}</span>
                                </div>
                                {task.listId === list.id && (
                                  <Check className="h-4 w-4 text-indigo-600" />
                                )}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {/* Create Cards Button */}
            <div className="bg-white rounded-2xl border border-gray-200 p-8 shadow-sm">
              <div className="flex items-center justify-between">
                <div className="space-y-3">
                  <h3 className="text-xl font-semibold text-gray-900">
                    Ready to create {enhancedTasks.filter(t => t.selected).length} cards
                  </h3>
                  <div className="flex items-center space-x-6 text-sm text-gray-600">
                    <span className="flex items-center">
                      <Tag className="h-4 w-4 mr-2" />
                      {boards.find(b => b.id === selectedBoard)?.name}
                    </span>
                    <span className="text-gray-400">•</span>
                    <span className="text-green-600 font-medium">
                      {enhancedTasks.filter(t => t.selected && t.listId).length} tasks ready
                    </span>
                    {enhancedTasks.filter(t => t.selected && !t.listId).length > 0 && (
                      <>
                        <span className="text-gray-400">•</span>
                        <span className="text-orange-600 font-medium">
                          {enhancedTasks.filter(t => t.selected && !t.listId).length} need list selection
                        </span>
                      </>
                    )}
                  </div>
                </div>
                
                <button
                  onClick={handleCreateCards}
                  disabled={creatingCards || enhancedTasks.filter(t => t.selected).length === 0 || enhancedTasks.filter(t => t.selected && !t.listId).length > 0}
                  className="px-8 py-4 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center font-semibold transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  {creatingCards ? (
                    <>
                      <Loader className="h-5 w-5 mr-3 animate-spin" />
                      Creating Cards...
                    </>
                  ) : (
                    <>
                      <Plus className="h-5 w-5 mr-3" />
                      Create Cards
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
} 